import { EventReportRequestParameters } from 'common/api/api.ts'
import { reportApi } from 'common/api/entitiesApi.tsx'
import dayjs from 'dayjs'
import { useCallback, useMemo, useState } from 'react'

import { downloadReport } from './exportReport.ts'
import { ReportColumn, loadReport } from './loadReport.ts'
import { buildKeyColumnOrder, buildSortPlan, compareValues } from './reportSort.ts'

export interface ReportRowObject {
  [key: string]: string | number | null | undefined
  __rowId: string
}

export interface ReportSortState {
  key?: string
  order?: 'ascend' | 'descend'
}

interface ReportState {
  loading: boolean
  hasSearched: boolean
  error?: string
  warning?: string
  keyColumnOrder: string[]
  columns: ReportColumn[]
  rawRows: any[][]
  totalLines: number
  totalValues?: Record<string, string | number>
  date: string | null
  sort: ReportSortState
}

const initialState: ReportState = {
  loading: false,
  hasSearched: false,
  warning: undefined,
  keyColumnOrder: [],
  columns: [],
  rawRows: [],
  totalLines: 0,
  totalValues: undefined,
  date: null,
  sort: {},
}

const mapRowsToObjects = (rows: any[][], columns: ReportColumn[]): ReportRowObject[] =>
  rows.map((row, rowIndex) => {
    return columns.reduce<ReportRowObject>(
      (acc, col, colIndex) => {
        acc[col.key] = row[colIndex]
        return acc
      },
      { __rowId: `${rowIndex}` },
    )
  })

export const useReportRunner = () => {
  const [state, setState] = useState<ReportState>(initialState)

  const generate = useCallback(
    async (payload: EventReportRequestParameters, options?: { keyColumnKeys?: string[] }) => {
      setState((prev) => ({
        ...prev,
        loading: true,
        hasSearched: true,
        error: undefined,
        warning: undefined,
        keyColumnOrder: options?.keyColumnKeys ?? [],
        columns: [],
        rawRows: [],
        totalLines: 0,
        totalValues: undefined,
      }))

      const result = await loadReport(payload, (request) => reportApi.report(request))

      if (result.kind === 'success') {
        setState((prev) => {
          const hasPrevSortKey = !!prev.sort.key && result.data.columns.some((col) => col.key === prev.sort.key)
          return {
            ...prev,
            loading: false,
            error: undefined,
            warning: result.data.warning,
            keyColumnOrder: buildKeyColumnOrder(result.data.columns, options?.keyColumnKeys ?? []),
            columns: result.data.columns,
            rawRows: result.data.rows,
            totalLines: result.data.totalLines,
            totalValues: result.data.totalValues,
            date: dayjs().format('HH:mm, DD MMM YYYY'),
            sort: hasPrevSortKey ? prev.sort : {},
          }
        })
        return
      }

      setState((prev) => ({
        ...prev,
        loading: false,
        error: result.message,
        warning: undefined,
        keyColumnOrder: [],
        columns: [],
        rawRows: [],
        totalLines: 0,
        totalValues: undefined,
      }))
    },
    [],
  )

  const onSort = useCallback((key?: string, order?: 'ascend' | 'descend') => {
    setState((prev) => ({
      ...prev,
      sort: { key, order },
    }))
  }, [])

  const sortedRows = useMemo(() => {
    const rows = [...state.rawRows]
    const sortPlan = buildSortPlan(state.columns, state.sort, state.keyColumnOrder)
    if (!sortPlan.length) {
      return rows
    }

    rows.sort((left, right) => {
      for (const criterion of sortPlan) {
        const delta = compareValues(left[criterion.index], right[criterion.index])
        if (delta !== 0) {
          return criterion.order === 'ascend' ? delta : -delta
        }
      }
      return 0
    })

    return rows
  }, [state.rawRows, state.columns, state.sort, state.keyColumnOrder])

  const rowObjects = useMemo(() => mapRowsToObjects(sortedRows, state.columns), [sortedRows, state.columns])

  const clearResults = useCallback(() => {
    setState((prev) => ({
      ...prev,
      loading: false,
      hasSearched: false,
      warning: undefined,
      error: undefined,
      keyColumnOrder: [],
      columns: [],
      rawRows: [],
      totalLines: 0,
      totalValues: undefined,
      sort: {},
      date: null,
    }))
  }, [])

  const exportReport = useCallback(async (payload: EventReportRequestParameters, format: 'csv' | 'xlsx') => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: undefined }))
      await downloadReport(format, payload)
      setState((prev) => ({ ...prev, loading: false }))
    } catch (error: any) {
      const message =
        typeof error?.message === 'string' ? error.message : 'Failed to export report. Please try narrowing the filters.'
      setState((prev) => ({ ...prev, loading: false, error: message }))
    }
  }, [])

  return {
    state,
    generate,
    onSort,
    clearResults,
    exportReport,
    rowObjects,
  } as const
}
